import { Component, ElementRef, Injector, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudDataSource, CrudListComponent, ROLE_METADATA, Role, RoleService, USER_DETAILS_VIEW_TYPE, USER_TYPE, User, UserDetailsView, UserDetailsViewQueryFilterFull, UserDetailsViewService, UserService, UserStorage } from '@scisys-uim/uim-common';
import { BusinessUnit, ASSETS_USER_DETAILS_TYPE, BUSINESS_UNIT_METADATA } from 'assets-model';
import { BusinessUnitService, AssetsUserDetailsService, UsersDataSource, AssetsUserViewService } from 'assets-service';


@Component({
  selector: 'app-user-search',
  templateUrl: './user-search.component.html',
  styleUrls: ['./user-search.component.scss']
})
export class UserSearchComponent extends CrudListComponent<UserDetailsView, string> {
  public searchForm: FormGroup;

  displayedColumns = ['username', 'details.attributes.businessUnit.buId', 'user.authTypes', 'user.externalAuthTypes', 'user.userTypes', 'user.roles', 'user.allRoles', 'user.enabled', 'user.systemProvided', 'actions'];
  disabledColumns: string[] = ['user.allRoles', 'user.userTypes', 'user.externalAuthTypes'];

  users: UserDetailsView[];

  roleSelectionList: Role[];
  roleFilter: Role;
  @ViewChild('roleFilterInput') roleFilterInput: MatSelect;

  businessUnitSelectionList: BusinessUnit[];
  businessUnitFilter: BusinessUnit;
  @ViewChild('businessUnitFilterInput') businessUnitFilterInput: MatSelect;

  nameFilter: string = '';
  @ViewChild('nameFilterInput', { static: true }) nameFilterInput: ElementRef;

  enabledFilter: boolean = true;
  @ViewChild('enabledFilterInput') enabledFilterInput: MatSelect;

  systemProvidedFilter: boolean = false;
  @ViewChild('systemProvidedFilterInput') systemProvidedFilterInput: MatSlideToggle;

  includeUsersInheritingRoleFilter: boolean = true;
  @ViewChild('includeUsersInheritingRoleFilterInput') includeUsersInheritingRoleFilterInput: MatSlideToggle;

  private config: any;
  reportRun: any = {};

  constructor(protected route: ActivatedRoute,
    protected router: Router,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    private userService: UserService,
    private userViewService: UserDetailsViewService,
    private assetsUserViewService: AssetsUserViewService,
    protected assetsUserDetailsService: AssetsUserDetailsService,
    protected roleService: RoleService,
    protected businessUnitService: BusinessUnitService,
    protected fb: FormBuilder,
    protected injector: Injector) {
    super(USER_DETAILS_VIEW_TYPE, route, router, userStorage, dialog, userViewService, injector);

    this.searchForm = this.fb.group({
      name: [null],
      businessUnit: [null],
      systemProvided: [false],
      enabled: [true],
      role: [null],
      includeUsersInheritingRole: [true],
      outputFormat: [true],
      download: [true]
    });
  }

  createServiceArguments() {
    const svcArgs: UserDetailsViewQueryFilterFull = {
      name: this.nameFilter,
      nameCaseSensitiveMatch: false,
      nameLikeType: 'CONTAINS',
      roles: this.roleFilter == null ? [] : [this.roleFilter.name],
      includeByRolesInherited: this.includeUsersInheritingRoleFilter,
      systemProvided: (!!this.systemProvidedFilter) ? null : false,
      enabled: this.enabledFilter,
      userDetailsFilter: {
        queryType: 'QueryRoot',
        filters: [
        ]
      }
    };

    if (this.businessUnitFilter) {
      svcArgs.userDetailsFilter.filters.push({
        queryType: 'StringEquals',
        attributeName: 'buId',
        value: this.businessUnitFilter.buId
      });
    }
    return svcArgs;
  }


  clearFilters(): void {
    this.nameFilter = null;
    this.businessUnitFilter = null;
    this.systemProvidedFilter = false;
    this.enabledFilter = true;
    this.roleFilter = null;
    this.includeUsersInheritingRoleFilter = true;
    super.clearFilters();
    this.loadPage();
  }

  createDataSource(): CrudDataSource<UserDetailsView> {
    return new UsersDataSource(this.userViewService);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.dataSource.data$.subscribe(
      data => {
        this.users = data;
      });
    function initOptions(config: any): any {
      config = config || {};
      config.export = config.export || {};
      config.export.options = config.export.options || {};
      return config.export.options;
    }
    this.config = initOptions({});
    this.configStorage.itemChanges().subscribe(config => {
      this.config = initOptions(config);
    });

    this.assetsUserViewService.exportReportSpecification().subscribe(spec => {
      if (!spec) {
        return;
      }
      this.reportRun.outputFormats = spec.outputFormatsSupported;
      this.reportRun.outputFormat = this.reportRun.outputFormats.find(s => s.name == "XLS") || spec.outputFormatsSupported[0];
      this.reportRun.download = !!this.reportRun.outputFormat.downloadOnly;
    });

    this.roleLoadSelectionList();
    this.businessUnitLoadSelectionList();

    // if we disable too soon, the column header display text won't be rendered, and the picker will have code names not display names.
    this.columns.$afterViewInit.subscribe(data => {
      this.columns.setColumnsEnabled(this.disabledColumns, false);
      // reset the defaults for the context so the picker Reset button doesn't re-enable the columns above
      this.columns.resetInitialDisplayColumns();
      // delayed wiring until context set, so that we don't start remembering changes too early
      this.restoreColumnState();
      this.rememberColumnState();
    });
  }

  exportAction() {
    return this.assetsUserViewService.exportByExtFiltersAction(this.createServiceArguments(),
      this.reportRun.outputFormat, this.reportRun.download, this.pageSpecification());
  }

  getMaxExportResults(): number {
    if (this.config?.export?.options && this.config.export.options.maxExportResults) {
      return this.config.export.options.maxExportResults;
    } else return 100;
  }

  isExportEnabled(): boolean {
    return this.reportRun && this.reportRun.download != null && this.reportRun.outputFormat != null
      && this.pageInfo && this.pageInfo.totalElements != null && this.pageInfo.totalElements > 0
      && (this.getMaxExportResults() == null || this.pageInfo.totalElements <= this.getMaxExportResults());
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.addSearchOnChangedInputEventListener(this.nameFilterInput);
    this.addSearchOnChangedSelectEventListener(this.roleFilterInput);
    this.addSearchOnChangedSelectEventListener(this.businessUnitFilterInput);
    this.addSearchOnChangedSlideToggleEventListener(this.systemProvidedFilterInput);
    this.addSearchOnChangedSelectEventListener(this.enabledFilterInput);
    this.addSearchOnChangedSlideToggleEventListener(this.includeUsersInheritingRoleFilterInput);
  }

  allowsDelete(item: UserDetailsView): boolean {
    return !item.user?.enabled && item.deletable && !this.isCurrentUser(item);
  }

  allowsEdit(item: UserDetailsView): boolean {
    return item.editable && !this.isCurrentUser(item);
  }

  allowsCreate() {
    return false; //this.userStorage?.currentUserHasAnyAuthority("CREATE_USER");
  }

  isCurrentUser(item: User | UserDetailsView): boolean {
    if (!item) {
      return false;
    }
    const currentUser = this.userStorage.get();
    if (currentUser) {
      return currentUser.username == item.username;
    }
    return false;
  }

  backRoute() {
    return ['/admin'];
  }

  displayRoles(roles: Role[]): string {
    return this.displayRolesWithFunction(roles, r => r.name);
  }

  roleLoadSelectionList() {
    this.roleService.findAll().subscribe(data => {
      var allRoles = data.items;
      if (!this.userStorage.currentUserHasSuperuserAuthority()) {
        // TODO - make work - ownPrivileges/allPrivileges for SUPERUSER role should have them all.
        //const superuserAuthority = this.userStorage.superuserAuthority;
        //const superUserRoles = roles.filter(r => r.allPrivileges.map(p => p.name).find(p => p == superuserAuthority) != null);
        const superUserRoleNames = ['SUPERUSER'];
        allRoles = allRoles.filter(r => !superUserRoleNames.includes(r.name));
      }
      this.roleSelectionList = allRoles;
    });
    this.role.enable();
  }

  roleChanged(event: Role, unset: boolean = true) {
    if (event) {
      this.includeUsersInheritingRole.enable();
    } else {
      this.includeUsersInheritingRole.disable();
    }
  }

  roleCompare(a: any, b: any) {
    return ROLE_METADATA.equals(a, b);
  }

  displayRolesWithFunction(roles: Role[], display: (role: Role) => string): string {
    if (!roles) {
      return '';
    }
    return roles.map(r => display(r)).join(', ');
  }

  businessUnitLoadSelectionList() {
    this.businessUnitService.findAll().subscribe(
      data => {
        this.businessUnitSelectionList = data.items;
      }
    );
  }

  businessUnitChanged(event: BusinessUnit, unset: boolean = true) {
  }

  businessUnitCompare(a: any, b: any) {
    return BUSINESS_UNIT_METADATA.equals(a, b);
  }

  // Assets User Details

  getSrpUserDetailsExcludedByExistingUserDetailsTypes(element: UserDetailsView): string[] {
    return this.assetsUserDetailsService.getUserDetailsExcludedByExistingUserDetailsTypes(element?.details);
  }

  isSrpUserDetailsExcludedByExistingUserDetailsType(element: UserDetailsView): boolean {
    return this.assetsUserDetailsService.isUserDetailsExcludedByExistingUserDetailsType(element?.details);
  }

  allowsViewSrpUserDetails(element: UserDetailsView): boolean {
    return this.userStorage.currentUserHasAnyAuthority(ASSETS_USER_DETAILS_TYPE.viewAuthorities);
  }

  enabledViewSrpUserDetails(element: UserDetailsView): boolean {
    return !this.isSrpUserDetailsExcludedByExistingUserDetailsType(element);
  }

  viewOrEditRouteToSrpUserDetails(element: UserDetailsView): any[] {
    if (!element) {
      return;
    }
    const keySegments = [element.username];
    if (!keySegments) {
      return;
    }
    var routeBase = ASSETS_USER_DETAILS_TYPE.viewRouteBase;
    if (!this.isCurrentUser(element) && this.userStorage.currentUserHasAnyAuthority(ASSETS_USER_DETAILS_TYPE.editAuthorities)) {
      routeBase = ASSETS_USER_DETAILS_TYPE.editRouteBase;
    }
    return [...routeBase, ...keySegments];
  }
  //////////////////////

  viewRouteBase(): any[] {
    return USER_TYPE.viewRouteBase || super.viewRouteBase();
  }
  editRouteBase(): any[] {
    return USER_TYPE.editRouteBase || super.editRouteBase();
  }

  get name() {
    return this.searchForm.get('name');
  }
  get businessUnit() {
    return this.searchForm.get('businessUnit');
  }
  get systemProvided() {
    return this.searchForm.get('systemProvided');
  }
  get enabled() {
    return this.searchForm.get('enabled');
  }
  get role() {
    return this.searchForm.get('role');
  }
  get includeUsersInheritingRole() {
    return this.searchForm.get('includeUsersInheritingRole');
  }
}

